import { Button, ButtonVariant, Input } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type FC } from 'react'
import { SelectDropdownIcon } from '../../components'
import { GenerateInvestmentWirePDFDownload } from '../../components/generate-pdf/generate-pdf'
import { AccountTransferField, type TransferListOption } from '../../types/form-model/form-model'

const log = new Logger('Receive Investment Wire Transfer')

export interface TabbedReceiveInvestmentWireProps {
  hasMultipleAccounts: boolean
  accountSelected?: TransferListOption
  isLoading: boolean
  openModal: () => void
}

export const TabbedReceiveInvestmentWire: FC<TabbedReceiveInvestmentWireProps> = ({
  hasMultipleAccounts,
  accountSelected,
  isLoading,
  openModal,
}) => {
  const { t } = useTranslation('features-transfer')

  const translationPrefix = 'tabbed_wire_transfer.receive_transfer'

  const routingNumber = t(`${translationPrefix}.apex_routing_number`)
  const apexAccountNumber = t(`${translationPrefix}.apex_account_number`)
  const accountNumber = accountSelected?.value
  const addressLine1 = t(`${translationPrefix}.apex_beneficiary_address_line1`)
  const addressLine2 = t(`${translationPrefix}.apex_beneficiary_address_line2`)
  const addressCity = t(`${translationPrefix}.apex_beneficiary_address_city`)
  const addressRegionCode = t(`${translationPrefix}.apex_beneficiary_address_region_code`)
  const addressPostalCode = t(`${translationPrefix}.apex_beneficiary_address_postal_code`)

  const memo = t(`${translationPrefix}.apex_enter_as_needed`, {
    accountTitle: accountSelected?.accountTitle ?? '',
    accountNumber,
  })

  const handleDownloadPDF = (): void => {
    try {
      GenerateInvestmentWirePDFDownload({
        accountNumber,
        accountTitle: accountSelected?.accountTitle ?? '',
        t,
      })
    } catch (err: unknown) {
      log.error('Error generating PDF', err)
    }
  }

  return (
    <div className="flex flex-col mb-2xl">
      <p className="typo-primary-base text-text-meta mt-md">{t(`${translationPrefix}.description`)}</p>
      {hasMultipleAccounts ? (
        <Input
          readOnly
          className="mt-lg"
          autoComplete="off"
          id={AccountTransferField.To}
          onFocus={openModal}
          label={t('tabbed_account_transfer.full_transfer.to')}
          icon={<SelectDropdownIcon onClick={openModal} isDisabled={false} />}
          value={accountSelected?.display.name ?? ''}
          disabled={isLoading}
        />
      ) : null}
      <div className="flex flex-col my-2xl gap-md">
        {/* Bank */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.bank_label`)}</p>
          <div className="w-2/3">
            <p className="typo-primary-base-semibold text-text-header w-2/3 inline">
              {t(`${translationPrefix}.bmo_harris_bank`)}
            </p>
          </div>
        </div>

        {/* Routing Number */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.routing_number_label`)}</p>
          <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">{routingNumber}</p>
        </div>
        {/* Account Number */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.account_number_label`)}</p>
          <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">{apexAccountNumber}</p>
        </div>
        {/* Beneficiary Name */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.beneficiary_label`)}</p>
          <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">
            {t(`${translationPrefix}.apex_beneficiary`)}
          </p>
        </div>
        {/* Beneficiary Address */}
        <div className="flex flex-row fs-exclude">
          <p className="typo-primary-base text-text-meta w-1/3">
            {t(`${translationPrefix}.beneficiary_address_label`)}
          </p>
          <div className="typo-primary-base-semibold text-text-header w-2/3">
            {addressLine1 ? <p>{addressLine1}</p> : null}
            {addressLine2 ? <p>{addressLine2}</p> : null}
            <div className="flex flex-row">
              {addressCity ? <span>{addressCity} &nbsp;</span> : null}
              {addressRegionCode ? <span>{addressRegionCode} &nbsp;</span> : null}
              {addressPostalCode ? <span>{addressPostalCode}</span> : null}
            </div>
          </div>
        </div>
        {/* Bank Address */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.bank_address_label`)}</p>
          <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">
            {t(`${translationPrefix}.apex_bank_address`)}
          </p>
        </div>
        {/* Memo */}
        <div className="flex flex-row">
          <p className="typo-primary-base text-text-meta w-1/3">{t(`${translationPrefix}.memo`)}</p>
          <p className="typo-primary-base-semibold text-text-header w-2/3 italic fs-exclude">{memo}</p>
        </div>

        <div className="flex flex-col gap-2xs">
          <Button
            variant={ButtonVariant.Secondary}
            className="w-full md:w-fit"
            onClick={handleDownloadPDF}
            isDisabled={Boolean(hasMultipleAccounts && !accountSelected)}
          >
            {t(`${translationPrefix}.download_wire_instructions`)}
          </Button>

          <p className="typo-primary-sm-light text-text-meta">{t(`${translationPrefix}.disclaimer`)}</p>
        </div>
      </div>
    </div>
  )
}
